import Cookie from 'js-cookie';
import {logo, logoSmall} from 'utils/getAppLogo';
import moment from 'moment-wrapper';
import BigNumber from 'bignumber.js';
import _ from 'lodash';
import {DEFAULT_APP_AUTH_TOKEN_KEY} from 'utils/api/cookieUtils';

_.noConflict();


export const SET_VAT_ON = 'SET_VAT_ON';
export const SET_OUTSOURCE_REQUEST_STATE = 'SET_OUTSOURCE_REQUEST_STATE';
export const SET_APP_CONFIG = 'SET_APP_CONFIG';
export const SET_AUTH_TOKEN = 'SET_AUTH_TOKEN';
export const SET_ROUTE_PARAMS = 'SET_ROUTE_PARAMS';
export const SET_APP_LOADING = 'SET_APP_LOADING';
export const SET_SHOW_BREADCRUMBS = 'SET_SHOW_BREADCRUMBS';
export const SET_ITEMS_ON_PAGE = 'SET_ITEMS_ON_PAGE';

const AORI_ENV_NAME = 'aori';
const ITEMS_ON_PAGE_KEY = 'items_on_page';
const DEFAULT_ITEMS_ON_PAGE = 10;

const buildVersion = BUILD_VERSION; // eslint-disable-line no-undef

const cookieValPerPage = Cookie.get(ITEMS_ON_PAGE_KEY);

export default {
  namespaced: true,
  state () {
    return {
      authToken: null,
      appLoading: false,
      vatOn: false,
      showOutsourceRequest: false,
      appConfig: {},
      showBreadcrumbs: true,
      routeParams: {},
      routeQueryParams: {},

      defaultParams: {
        project: {
          manage_placements: true
        }
      },

      itemsOnPage: cookieValPerPage ? parseInt(cookieValPerPage) : DEFAULT_ITEMS_ON_PAGE
    };
  },

  getters: {
    appLoading (state) {
      return state.appLoading;
    },
    vatOn (state) {
      return state.vatOn;
    },
    getVatForDate (state, getters, rootState, rootGetters) {
      return function (date) {
        const defaultVat = 1;

        const account = rootGetters['sellerUser/currentAccount'];
        if (!account) {
          return defaultVat;
        }
        const vatRates = account.vats || [];

        const targetDate = moment(date);

        for (let i = vatRates.length - 1; i >= 0; i--) {
          const vat = vatRates[i];
          if (moment(vat.date) <= targetDate) {
            return new BigNumber(vat.value);
          }
        }
        console.warn('[VAT] Rate not found for {0}. Using 1 as VAT'.format(date.format('YYYY-MM-DD')));
        return new BigNumber(defaultVat);
      };
    },
    vatRate (state, getters) {
      return getters.getVatForDate(new Date());
    },
    showOutsourceRequest (state, getters, rootState, rootGetters) {
      return rootGetters['user/canUser']('CREATE_SERVICE_STORE_TASKS');
    },
    appName: (state) => state.appConfig.APP_NAME,

    isDevelopment () {
      // eslint-disable-next-line no-undef
      return DEBUG;
    },
    activeCurrencyId () {
      return 1;
    },
    activeCurrency () {
      return {
        code: 'RUB',
        full_name: 'RUB (Russian Ruble)',
        id: 1,
        name: 'Russian Ruble',
        symbol: '₽'
      };
    },
    activeCurrencySymbol () {
      return '₽';
    },

    hasHelpUrl: (state, getters, rootState, rootGetters) => Boolean(rootGetters['user/currentUser'].help_url),
    appConfig: state => state.appConfig,
    showBreadcrumbs: state => state.showBreadcrumbs,
    appEnvName: state => state.appConfig.ENVIRONMENT,
    isAori: (state, getters) => getters.appEnvName === AORI_ENV_NAME,
    mccNestingLevel: state => state.appConfig.MCC_NESTING_LEVEL,
    authToken: state => state.authToken || Cookie.get(DEFAULT_APP_AUTH_TOKEN_KEY),
    routeParams: state => state.routeParams,
    appLogo (state, getters, rootState, rootGetters) {
      const user = rootGetters['user/currentUser'];

      let _logo = logo;
      let _logoSmall = logoSmall;

      if (user) {
        _logo = `/img/${user.logo}`;
        _logoSmall = `/img/${user.logo_small}`;
      }

      return {
        logo: _logo,
        logoSmall: _logoSmall
      };
    },
    basePath () {
      if (buildVersion) {
        return `/static/${buildVersion}/`;
      }
      return '/static/default/';
    },
    enableDoodle (state, getters) {
      // disable doodle specially for this app profile names
      const disableProfileNames = ['Tochka by Marilyn'];

      if (getters.appConfig.ENABLE_DOODLE) {
        const instanceName = getters.appConfig.APP_NAME;
        return !disableProfileNames.includes(instanceName);
      }
      return false;
    },
    googleSheetsServiceAccountEmail: () => 'sheets@marilyn-tech.iam.gserviceaccount.com',
    defaultParams (state) {
      return _.merge(
        state.defaultParams,
        state.appConfig.DEFAULT_PARAMS || {}
      );
    },

    itemsOnPage: (state) => state.itemsOnPage
  },

  mutations: {
    [SET_APP_LOADING] (state, val) {
      state.appLoading = val;
    },
    [SET_VAT_ON] (state, val) {
      state.vatOn = val;
    },
    [SET_OUTSOURCE_REQUEST_STATE] (state, val) {
      state.showOutsourceRequest = val;
    },
    [SET_SHOW_BREADCRUMBS] (state, val) {
      state.showBreadcrumbs = val;
    },
    [SET_APP_CONFIG] (state, val) {
      const oldConfig = state.appConfig;
      state.appConfig = {...oldConfig, ...val};
    },
    [SET_ROUTE_PARAMS] (state, val) {
      state.routeParams = val;
    },
    [SET_AUTH_TOKEN] (state, val) {
      state.authToken = val;
    },
    [SET_ITEMS_ON_PAGE] (state, val) {
      state.itemsOnPage = val;
      Cookie.set(ITEMS_ON_PAGE_KEY, val);
    }
  },

  actions: {
    [SET_APP_LOADING] (context, val) {
      context.commit(SET_APP_LOADING, val);
    },
    [SET_VAT_ON] (context, val) {
      context.commit(SET_VAT_ON, val);
    },
    [SET_OUTSOURCE_REQUEST_STATE] (context, val) {
      context.commit(SET_OUTSOURCE_REQUEST_STATE, val);
    },
    [SET_APP_CONFIG] (context, val) {
      context.commit(SET_APP_CONFIG, val);
    },
    [SET_SHOW_BREADCRUMBS] (context, val) {
      context.commit(SET_SHOW_BREADCRUMBS, val);
    },
    [SET_ROUTE_PARAMS] (context, val) {
      context.commit(SET_ROUTE_PARAMS, val);
    },
    [SET_ITEMS_ON_PAGE] (context, val) {
      context.commit(SET_ITEMS_ON_PAGE, val);
    }
  }
};
